/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import { useStorage } from '../hooks/use-storage';
import { en } from '../i18n/translations/en';
import { he } from '../i18n/translations/he';

// Initialize i18next
i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    he: { translation: he },
  },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

// Define supported languages
export type Language = 'en' | 'he';

// Language display names
export const LANGUAGES: Record<Language, string> = {
  en: 'English',
  he: 'עברית',
};

// Storage key
const LANGUAGE_STORAGE_KEY = 'user-language';

// Define context type
interface LanguageContextType {
  language: Language;
  t: (scope: string, options?: object) => string;
  setLanguage: (language: Language) => Promise<void>;
  isRTL: boolean;
}

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language] = useState<Language>('en'); // Always English
  const [isRTL] = useState(false); // Always LTR for English
  const storage = useStorage();

  // Function to change language - disabled but kept for future restoration
  const changeLanguage = useCallback(
    async (newLanguage: Language) => {
      // Language switching disabled - always use English
      console.info(
        'Language change requested (disabled - English only):',
        newLanguage
      );

      // Keep the storage logic intact for future use, but don't actually change
      try {
        await storage.setItem(LANGUAGE_STORAGE_KEY, 'en'); // Always save 'en'
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    },
    [storage]
  );

  // Initialize language - always English (but keep storage logic for future restoration)
  useEffect(() => {
    const initLanguage = async () => {
      try {
        // Always use English, but still read storage for future restoration logic
        const storedLanguage = await storage.getItem(LANGUAGE_STORAGE_KEY);
        console.info(
          'Stored language preference:',
          storedLanguage,
          '(forced to English)'
        );

        // Force English initialization
        await i18n.changeLanguage('en');

        // Set document properties for English
        if (typeof document !== 'undefined') {
          document.documentElement.dir = 'ltr';
          document.documentElement.lang = 'en';
        }

        // Save English to storage (overwrite any other preference)
        await storage.setItem(LANGUAGE_STORAGE_KEY, 'en');
      } catch (error) {
        console.error('Error initializing language:', error);
        // Ensure English as fallback
        await i18n.changeLanguage('en');
      }
    };

    void initLanguage();
  }, [storage]);

  // Translation function
  const t = (scope: string, options = {}) => {
    try {
      return i18n.t(scope, options);
    } catch (error) {
      console.error(`Translation error for key "${scope}":`, error);

      return scope; // Return the key as fallback
    }
  };

  const contextValue: LanguageContextType = {
    language,
    t,
    setLanguage: changeLanguage,
    isRTL,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};
